import { useMemo, useState } from 'react'
import { ASSET_HI_LOGO, TREE_SLOTS } from '../constants'
import type { EmpresaGrupo, TreeSlot } from '../types'
import { logoPlateClass, onImgError, sortEmpresas } from '../utils'

interface EmpresasTreeProps {
  empresas: EmpresaGrupo[]
  onSelectEmpresa: (slug: string) => void
}

const GHI_PATHS = [
  'M550 666V160',
  'M535 668V500L220 185H187',
  'M520 667V535L192 330H143',
  'M505 667V572L220 471H165',
  'M490 667V609L330 583H308',
  'M565 668V500L880 185H913',
  'M580 667V535L908 330H957',
  'M595 667V572L880 471H935',
  'M610 667V609L770 583H792',
  'M550 600L700 450V290L610 210',
]

export function EmpresasTree({ empresas, onSelectEmpresa }: EmpresasTreeProps) {
  const paused =
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false

  const { root, foco, ordered, siteUrl } = useMemo(() => {
    const list = sortEmpresas(empresas)
    const rootEmp =
      list.find((e) => e.eh_holding) || list.find((e) => !e.parent_slug) || list[0]
    const filhas = list
      .filter((e) => e.slug !== rootEmp?.slug && !e.eh_holding)
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
    const focoEmp =
      filhas.find((e) => e.destaque) ||
      filhas.find((e) => e.slug === 'lavanderia-60-minutos') ||
      filhas[0]
    const demais = filhas.filter((e) => e !== focoEmp)
    return {
      root: rootEmp,
      foco: focoEmp,
      ordered: focoEmp ? [focoEmp, ...demais] : demais,
      siteUrl: rootEmp?.site_url || 'https://grupohi.com.br/',
    }
  }, [empresas])

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const effectiveSlug = selectedSlug || foco?.slug || root?.slug || ''

  const handleSelect = (e: EmpresaGrupo) => {
    setSelectedSlug(e.slug)
    onSelectEmpresa(e.slug)
  }

  const rootPlate = logoPlateClass(root?.slug || 'grupo-hi')

  return (
    <div
      className={`cr-ghi${paused ? ' cr-ghi--paused' : ''}`}
      aria-label="Árvore interativa de marcas do Grupo HI"
    >
      <section className="cr-ghi-scene" aria-label="Árvore de marcas">
        <svg viewBox="0 0 1100 760" aria-hidden="true">
          <circle className="cr-ghi-halo" cx="550" cy="370" r="255" />
          <circle className="cr-ghi-halo" cx="550" cy="370" r="315" />
          <g className="cr-ghi-botanical">
            <path className="cr-ghi-leaf" d="M428 495C307 480 251 397 279 288C389 308 449 395 428 495Z" />
            <path className="cr-ghi-vein" d="M428 495L279 288M393 443L313 416M367 398L364 334M337 357L293 345" />
            <path className="cr-ghi-leaf" d="M495 313C424 291 392 237 410 176C480 193 509 246 495 313Z" />
            <path className="cr-ghi-vein" d="M495 313L410 176M470 272L426 258M451 238L454 206" />
          </g>
          <g className="cr-ghi-botanical cr-ghi-botanical--right">
            <path className="cr-ghi-leaf" d="M672 495C793 480 849 397 821 288C711 308 651 395 672 495Z" />
            <path className="cr-ghi-vein" d="M672 495L821 288M707 443L787 416M733 398L736 334M763 357L807 345" />
            <path className="cr-ghi-leaf" d="M605 313C676 291 708 237 690 176C620 193 591 246 605 313Z" />
            <path className="cr-ghi-vein" d="M605 313L690 176M630 272L674 258M649 238L646 206" />
          </g>
          <g id="cr-ghi-paths">
            {GHI_PATHS.map((d) => (
              <path key={d} className="cr-ghi-branch" d={d} />
            ))}
          </g>
          <g className="cr-ghi-fine" fill="none">
            <path d="M478 660V625L310 625L260 670H200" />
            <path d="M622 660V625L790 625L840 670H900" />
            <path d="M520 445L350 275V170L310 130" />
            <path d="M580 445L750 275V170L790 130" />
            <path d="M470 529L335 440H275V390" />
            <path d="M630 529L765 440H825V390" />
          </g>
          <g id="cr-ghi-flows">
            {GHI_PATHS.map((d, i) => (
              <path
                key={`flow-${d}`}
                className="cr-ghi-flow"
                d={d}
                style={{ animationDelay: `${-i * 0.55}s` }}
              />
            ))}
          </g>
          <circle className="cr-ghi-node cr-ghi-node--accent" cx="550" cy="160" r="11" />
          <circle className="cr-ghi-node" cx="310" cy="130" r="8" />
          <circle className="cr-ghi-node" cx="790" cy="130" r="8" />
          <circle className="cr-ghi-node cr-ghi-node--accent" cx="275" cy="390" r="7" />
          <circle className="cr-ghi-node cr-ghi-node--accent" cx="825" cy="390" r="7" />
          <circle className="cr-ghi-node" cx="200" cy="670" r="7" />
          <circle className="cr-ghi-node" cx="900" cy="670" r="7" />
        </svg>

        {ordered.map((e, i) => {
          const slot: TreeSlot = TREE_SLOTS[i] || {
            left: 20 + (i % 4) * 20,
            top: 20 + Math.floor(i / 4) * 18,
            row: Math.floor(i / 2),
          }
          const hero = i === 0 && !!foco
          const plate = logoPlateClass(e.slug)
          const label = e.nome_curto || e.nome
          return (
            <button
              key={e.slug}
              type="button"
              className={`cr-ghi-brand${hero ? ' cr-ghi-brand--hero' : ''}`}
              style={{
                left: `${slot.left}%`,
                top: `${slot.top}%`,
                ['--row' as string]: String(slot.row ?? 0),
              }}
              title={`${e.nome}${e.tagline ? ` — ${e.tagline}` : ''}`}
              aria-label={label}
              aria-pressed={effectiveSlug === e.slug}
              onClick={() => handleSelect(e)}
            >
              {hero && <span className="cr-ghi-featured">Marca em destaque</span>}
              <span className={`cr-ghi-brand-logo ${plate}`}>
                <img
                  src={e.logo_url}
                  alt={label}
                  width={120}
                  height={52}
                  loading="lazy"
                  onError={onImgError('/assets/empresas/grupo-hi.png')}
                />
              </span>
              <small>{e.tagline || 'Grupo HI'}</small>
            </button>
          )
        })}

        <div className="cr-ghi-brand cr-ghi-brand--root" title={root?.nome || 'Grupo HI'}>
          <span className={`cr-ghi-brand-logo ${rootPlate}`}>
            <img
              src={root?.logo_url || '/assets/empresas/grupo-hi.png'}
              alt="Grupo HI"
              width={140}
              height={56}
              loading="lazy"
              onError={onImgError(ASSET_HI_LOGO)}
            />
          </span>
          <small>{root?.tagline || 'A origem das conexões'}</small>
          <div className="cr-ghi-root-actions">
            <a className="cr-ghi-link" href="#/">
              Ver vagas
            </a>
            <a className="cr-ghi-link" href={siteUrl} target="_blank" rel="noopener noreferrer">
              Site
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
